import './styles/ListingDetail.scss';

import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Button, Grid } from '@mui/material';

import parse from 'html-react-parser';
import { FunctionComponent, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import DELETE_MUTATION from 'gql/mutations/deleteListing.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import QUERY from 'gql/queries/getListing.gql';

import CustomCarousel from 'app/components/Carousel';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import DetailPage from 'app/components/DetailPage';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import Belongings from 'assets/belongings.svg';
import Flat from 'assets/flat.svg';
import Study from 'assets/materials.svg';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

import Listing from '../Listing';
import ListingSideInfo from './components/ListingSideInfo';

interface Props {}

export const ListingDetail: FunctionComponent<Props> = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const client = useApolloClient();
  const userData = client.readQuery({ query: USER_QUERY });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteListing] = useMutation(DELETE_MUTATION);
  const aclHelper = new ACLHelper(userData.currentUser);

  usePageHeading({
    heading: t(translations.layout.menu.bulletinBoard()),
    headingProps: { back: '/bulletin' }
  });

  const getListingQuery = useQuery<any, any>(QUERY, {
    variables: { id: parseInt(id!, 10) }
  });

  const getTypeIcon = type => {
    switch (type) {
      case 'study':
        return (
          <img className="listing-detail__content__main__type-icon" src={Study} alt="study icon" />
        );
      case 'flat':
        return (
          <img
            className="listing-detail__content__main__type-icon"
            src={Flat}
            alt="flat rent icon"
          />
        );
      case 'belongings':
      default:
        return (
          <img
            className="listing-detail__content__main__type-icon"
            src={Belongings}
            alt="belongings icon"
          />
        );
    }
  };

  const handleDelete = async () => {
    if (deleting) {
      return;
    }
    setDeleting(true);
    try {
      await deleteListing({
        variables: {
          id
        },
        refetchQueries: ['GetListings']
      });
      dispatch(AlertActions.success(t(translations.pages.listingDetail.deleteSuccessful())));
      navigate('/bulletin', { replace: true });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.listingDetail.deleteFailed())));
    }
    setDeleting(false);
  };

  const cancelListingCreation = (event?, reason?) => {
    if (reason && reason === 'backdropClick') return;
    if (disabledCancel) {
      return;
    }
    setDisabledCancel(true);
    setCreateModalOpen(false);
    setDisabledCancel(false);
  };

  const editHandlers = listing => (
    <Grid container justifyContent="flex-end" gap={1}>
      <Button
        color="primary"
        variant="contained"
        disabled={deleting}
        onClick={() => setCreateModalOpen(true)}
        style={{ color: 'white', marginBottom: '1em' }}
      >
        {t(translations.common.edit())}
      </Button>
      <span
        style={{
          position: 'relative',
          marginBottom: '1em',
          marginLeft: '1em'
        }}
      >
        <Button
          color="primary"
          variant="contained"
          onClick={() => setCancelModalOpen(true)}
          disabled={deleting}
          style={{
            color: 'white',
            backgroundColor: !deleting ? 'red' : undefined
          }}
        >
          {t(translations.common.delete())}
        </Button>
        {deleting && <SubmittingLoader />}
      </span>
      <ErrorBoundary boundaryTag="listingDetailBoundary">
        <Modal
          open={createModalOpen}
          onClose={cancelListingCreation}
          headline={t(translations.pages.listings.create.title())}
          onCancel={() => {
            cancelListingCreation();
          }}
          disabledCancel={disabledCancel}
        >
          <Listing
            onCancel={() => cancelListingCreation()}
            closeHandler={() => cancelListingCreation()}
            listing={listing}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
        <ConfirmationDialog
          open={cancelModalOpen}
          title={t(translations.pages.listingDetail.deleteConfirm.title())}
          onClose={() => {
            setCancelModalOpen(false);
          }}
          onConfirm={() => {
            setCancelModalOpen(false);
            handleDelete();
          }}
        >
          <div>{t(translations.pages.listingDetail.deleteConfirm.message())}</div>
        </ConfirmationDialog>
      </ErrorBoundary>
    </Grid>
  );

  return (
    <GQLGuard
      query={getListingQuery}
      onError={error => {
        if (error.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(AlertActions.error(`${t(translations.pages.listings.notFound())}`));
        } else {
          dispatch(
            AlertActions.error(`${t(translations.pages.listings.openFailed())}: ${error.message}`)
          );
        }
        navigate('/bulletin', { replace: true });
        return null;
      }}
    >
      {data => {
        const { listing } = data;
        const canUserEdit =
          aclHelper.isSuperUser() || userData.currentUser.uid === listing.author.id;
        return (
          <>
            <Helmet>
              <title>{listing.title}</title>
            </Helmet>
            {canUserEdit && <>{editHandlers(listing)}</>}
            <DetailPage sideInfo={<ListingSideInfo listing={listing} />} className="listing-detail">
              <Grid container className="listing-detail__content">
                <Grid item xs className="listing-detail__content__main">
                  <Grid container>
                    <Grid item xs style={{ alignSelf: 'center' }}>
                      <Grid container className="listing-detail__content__main__headline">
                        {listing.title}
                      </Grid>
                    </Grid>
                    <Grid item xs="auto">
                      {getTypeIcon(listing.type)}
                    </Grid>
                  </Grid>
                  {listing.photos && listing.photos.length > 0 && (
                    <Grid container justifyContent="center">
                      <Grid item xs={12} lg={6} className="listing-detail__content__main__photos">
                        <CustomCarousel
                          path="bulletin-board/files"
                          images={listing.photos}
                          imageSize="large"
                          itemsToShow={3}
                        />
                      </Grid>
                    </Grid>
                  )}
                  <Grid container className="listing-detail__content__main__offer-text">
                    <Grid>{parse(listing.offerText)}</Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DetailPage>
          </>
        );
      }}
    </GQLGuard>
  );
};
